import { Injectable } from '@angular/core';
import { FormActionHandlerService } from './base.service';
import { FormStatusService } from '../status.service';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { FormDataService } from '../data.service';
import { FormEmailService } from '../email.service';
import { UserContextService } from '../../../user/services/user-context.service';
import { formStatus } from '../../form.constants';
import { FormShareService } from '../share.service';
import { PublishSubscribeService } from '../../../fastlane-common/services/publish-subscribe.service';
import { events } from '../../../fastlane-common/event/event.constants';
import { EventContext } from '../../../fastlane-common/event/interfaces/event-context.interface';
import {
  dateTypes,
  contactType,
  incentiveProgram
} from '../../../project/project.constants';
import { AuditChange } from '../../../fastlane-common/event/interfaces/audit-change';
import { Project as FilmProject } from '../../../entertainment/models/film/project.model';
import { Project as DigitalProject } from '../../../entertainment/models/digital/project.model';
import { setStartoftheDayTime } from '../../../project/project.functions';
import { Audit as FilmAudit } from '../../../entertainment/models/film/audit.model';
import { Audit as DigitalAudit } from '../../../entertainment/models/digital/audit.model';

@Injectable({
  providedIn: 'root'
})
export class CompleteAuditActionHandlerService extends FormActionHandlerService {
  constructor(
    private dataService: FormDataService,
    private emailService: FormEmailService,
    private statusService: FormStatusService,
    private shareService: FormShareService,
    private userContext: UserContextService,
    private swal: SwalService,
    private pubSubService: PublishSubscribeService
  ) {
    super();
  }

  exec(): Promise<any> {
    const that = this;
    return new Promise((resolve, reject) => {
      that.swal.load('Submitting this form to LED for review...');

      // Change the status to received
      that.statusService.changeStatus(formStatus.received);
      // Add Verification Report Received Date
      that.dataService.addAuditFormDate(
        dateTypes.verificationReportReceivedDate,
        that.shareService.formId,
        that.userContext.currentUser.id
      );
      // Add Form Critical Date
      let swalTitle = 'Audit Submitted';
      if (
        that.shareService.currentIncentiveProgram === incentiveProgram.film.code
      ) {
        swalTitle = 'Audit Submitted';
        const audit = <FilmAudit>that.shareService.form;
        audit.auditCriticalDates.verificationReportReceivedDate = setStartoftheDayTime(
          new Date()
        );
      }
      if (
        that.shareService.currentIncentiveProgram === incentiveProgram.dm.code
      ) {
        swalTitle = 'EVR Submitted';
        const audit = <DigitalAudit>that.shareService.form;
        audit.auditCriticalDates.verificationReportReceivedDate = setStartoftheDayTime(
          new Date()
        );
      }
      // Save the form and generate the pdf snapshot
      that.dataService.saveForm().subscribe(
        successfulSave => {
          that.raiseAuditCompleteEvent('');
          that.shareService.refreshPage();
          that.swal.success({
            title: swalTitle,
            text: 'The program manager has been notified'
          });
        },
        errorSaving =>
          reject(
            'We were unable to submit your form to LED for review. Please try again later.'
          )
      );
    });
  }

  raiseAuditCompleteEvent(auditingCompanyName: string) {
    const project = this.shareService.projectBase;

    const filmProject = project as FilmProject;
    const digitalProject = project as DigitalProject;

    const form = this.shareService.auditInfo;

    // Select the one that is not null
    const entId =
      filmProject.projectInfo.filmId || digitalProject.projectInfo.digitalId;

    // Raise Event to notify of auditor change
    this.pubSubService.raise<EventContext<AuditChange>>(
      events.auditManagementChange.code,
      formStatus.received,
      <EventContext<AuditChange>>{
        eventType: events.auditManagementChange.code,
        eventAction: formStatus.auditorAssigned,
        data: <AuditChange>{
          auditingCompanyName: auditingCompanyName,
          auditingCompanyId: form.auditingCompanyId,
          authorizedRepresentative: project.projectContacts.find(
            c => c.contactType === contactType.signee.code
          ),
          auditForm: form,
          contacts: project.projectContacts,
          entertainmentIdType: project.projectInfo.incentiveProgram,
          entertainmentId: entId,
          incentiveProgram: project.projectInfo.incentiveProgram,
          projectGuid: project.id,
          projectId: project.projectInfo.projectId,
          projectInfoBase: project.projectInfo,
          projectName: project.projectInfo.projectName,
          season: form.season
        }
      }
    );
  }
}
